
import { Admin } from '@/types/admin.types';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { getHeaderHtml, getFooterHtml, getCommonPrintStyles, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";

export const printAdminProfile = (admin: Admin) => {
  // Legacy function - now calls JPG version for backward compatibility
  return printAdminProfileAsJPG(admin);
};

export const printAdminProfileAsJPG = async (admin: Admin) => {
  try {
    const loadingToast = toast.loading('Generating admin profile JPG...');

    const { content, filename, styles } = generateAdminProfileContent(admin);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download admin profile');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Admin profile downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading admin profile:', error);
    toast.error('Failed to download admin profile');
  }
};

export const printAdminProfileAsPDF = async (admin: Admin) => {
  try {
    const loadingToast = toast.loading('Generating admin profile PDF...');

    const { content, filename, styles } = generateAdminProfileContent(admin);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download admin profile');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Admin profile downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading admin profile:', error);
    toast.error('Failed to download admin profile');
  }
};

export const printAdminsListAsJPG = async (adminsList: Admin[]) => {
  try {
    const loadingToast = toast.loading('Generating admins list JPG...');
    
    const { content, filename, styles } = generateAdminsListContent(adminsList);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download admins list');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Admins list downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading admins list:', error);
    toast.error('Failed to download admins list');
  }
};

export const printAdminsListAsPDF = async (adminsList: Admin[]) => {
  try {
    const loadingToast = toast.loading('Generating admins list PDF...');
    
    const { content, filename, styles } = generateAdminsListContent(adminsList);
    const printDocument = createPrintDocument(content, filename, styles);
    
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download admins list');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Admins list downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading admins list:', error);
    toast.error('Failed to download admins list');
  }
};

// Legacy function - now calls JPG version for backward compatibility
export const printAdminsList = (adminsList: Admin[]) => {
  return printAdminsListAsJPG(adminsList);
};

const generateAdminProfileContent = (admin: Admin) => {
  const profileContent = `
    <div class="profile-container">
      <div class="avatar-section">
        ${
          admin.profileImage
            ? `<img src="${admin.profileImage}" class="admin-avatar" alt="${admin.firstName} ${admin.lastName} profile" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />`
            : ''
        }
        <div class="img-fallback" style="${admin.profileImage ? 'display:none;' : ''}">
          ${admin.firstName?.[0] || ''}${admin.lastName?.[0] || ''}
        </div>
      </div>
      <div class="main-info">
          <div>
            <div class="admin-name">${admin.firstName} ${admin.lastName}</div>
            <div class="admin-id">Admin ID: ${admin.id}</div>
          </div>
          <div class="section">
            <div class="section-title">Basic Information</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Email:</span> ${admin.email}
              </div>
              <div class="info-item">
                <span class="label">Phone:</span> ${admin.phone}
              </div>
              <div class="info-item">
                <span class="label">Role:</span> ${admin.role}
              </div>
              <div class="info-item">
                <span class="label">Status:</span> ${admin.status}
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Contact Information</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Country:</span> ${admin.country}
              </div>
              <div class="info-item">
                <span class="label">City:</span> ${admin.city}
              </div>
              <div class="info-item">
                <span class="label">Address:</span> ${admin.address}
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Qualifications</div>
            ${admin.qualifications?.map(qual => `
              <div class="info-item">
                <span class="label">${qual.degree}</span>
                <span>${qual.institution} (${qual.year})</span>
                <span> | Field: ${qual.field}</span>
              </div>
            `).join('') || 'No qualifications listed'}
          </div>
      </div>
    </div>
  `;
  
  const title = 'Administrator Profile';
  const styles = `
    ${getCommonPrintStyles()}
    .profile-container {
      margin-top: 20px;
      display: flex;
      gap: 32px;
      align-items: flex-start;
    }
    .avatar-section {
      min-width: 120px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .admin-avatar {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      border: 4px solid #f5f5f5;
      object-fit: cover;
      background: #FEC6A1;
      margin-bottom: 12px;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    .img-fallback {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      font-size: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      background: #D946EF;
      font-weight: bold;
      margin-bottom: 12px;
      border: 4px solid #f5f5f5;
    }
    .main-info {
      flex: 1;
      background: #FDE1D3;
      border-radius: 12px;
      padding: 24px 20px 16px 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .section {
      margin-bottom: 6px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
    }
    .section:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    .section-title {
      font-size: 1.1em;
      font-weight: 600;
      color: #6E59A5;
      margin-bottom: 6px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 7px 30px;
    }
    .info-item {
      font-size: 0.97em;
      margin-bottom: 3px;
      color: #444;
    }
    .label {
      font-weight: 500;
      color: #8E9196;
      margin-right: 4px;
    }
    .admin-name {
      font-size: 1.5em;
      font-weight: bold;
      margin-bottom: 2px;
      color: #3D7069;
      letter-spacing: 0.02em;
    }
    .admin-id {
      font-size: 0.95em;
      color: #888;
      margin-bottom: 9px;
      font-weight: 500;
    }
    .header-logo {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
  `;

  const fullContent = `
    <div class="page-content">
      ${getHeaderHtml(title)}
      ${profileContent}
      ${getSignatureHtml('admin')}
      ${getFooterHtml('admin')}
    </div>
  `;
  
  const filename = `Admin_Profile_${admin.firstName}_${admin.lastName}`;
  
  return { 
    content: fullContent, 
    filename, 
    styles 
  };
};

const generateAdminsListContent = (adminsList: Admin[]) => {
  const today = new Date();
  const formattedDate = format(today, 'M/d/yyyy');
  
  const tableContent = `
    <div style="text-align:center;font-size:.97em;font-weight:500;margin-bottom:20px">
      Total Administrators: ${adminsList.length}
    </div>
    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Position</th>
          <th>Role</th>
          <th>Email</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${adminsList.map((admin, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${admin.firstName} ${admin.lastName}</td>
            <td>${admin.position || 'N/A'}</td>
            <td>${admin.role}</td>
            <td>${admin.email}</td>
            <td>${admin.status}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  const title = 'Administrators List';
  const styles = `
    ${getCommonPrintStyles()}
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-top: 20px; 
    }
    th, td { 
      padding: 8px; 
      text-align: left; 
      border-bottom: 1px solid #e5e7eb; 
    }
    th { 
      background-color: #f3f4f6; 
      color: #374151; 
      font-weight: bold;
    }
    tr:nth-child(even) { 
      background-color: #f9fafb; 
    }
    .header-logo {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
  `;

  const fullContent = `
    <div class="page-content">
      ${getHeaderHtml(title)}
      ${tableContent}
      ${getSignatureHtml('admin')}
      ${getFooterHtml('admin')}
    </div>
  `;
  
  const filename = `Administrators_List_${formattedDate}`;
  
  return {
    content: fullContent,
    filename,
    styles
  };
};
